import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

const INDEXED_COLORS = {
  pending:  { bg: '#fef9c3', color: '#854d0e', label: 'Not Indexed' },
  indexing: { bg: '#dbeafe', color: '#1e40af', label: 'Indexing…'  },
  indexed:  { bg: '#f0fdf4', color: '#166534', label: 'Indexed'    },
  failed:   { bg: '#fef2f2', color: '#991b1b', label: 'Index Failed'},
};
const STATUS_COLORS = {
  active:    { bg: '#f0fdf4', color: '#166534', label: 'Active'     },
  matching:  { bg: '#fef9c3', color: '#854d0e', label: 'Matching…'  },
  completed: { bg: '#eff6ff', color: '#1e40af', label: 'Matched'    },
  published: { bg: '#f5f3ff', color: '#5b21b6', label: 'Published'  },
};

const SuperAdminDashboard = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', date: '', venue: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/superadmin/events`)
      .then(res => setEvents(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError('');
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/events`, form);
      setShowCreate(false);
      setForm({ name: '', date: '', venue: '', description: '' });
      navigate(`/admin/super/events/${res.data.id}`);
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create event.');
    } finally {
      setCreating(false);
    }
  };

  const filtered = filter === 'all' ? events : events.filter(e => {
    if (filter === 'pending')   return e.indexed_status === 'pending';
    if (filter === 'indexed')   return e.indexed_status === 'indexed';
    if (filter === 'matched')   return e.latest_run_status === 'completed';
    if (filter === 'published') return !!e.published_at;
    return true;
  });

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.pageHeader}>
          <div>
            <h1 style={styles.pageTitle}>All Events</h1>
            <p style={styles.pageSubtitle}>{events.length} events across all photographers</p>
          </div>
          <button style={styles.createBtn} onClick={() => { setCreateError(''); setShowCreate(true); }}>
            + Create Event
          </button>
        </div>

        {/* Create Event Modal */}
        {showCreate && (
          <div style={styles.overlay} onClick={() => setShowCreate(false)}>
            <div style={styles.modal} onClick={e => e.stopPropagation()}>
              <h2 style={styles.modalTitle}>Create New Event</h2>
              <form onSubmit={handleCreate}>
                <Field label="Event Name *">
                  <input style={styles.input} placeholder="e.g. Smith & Jones Wedding"
                    value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
                </Field>
                <Field label="Event Date *">
                  <input type="date" style={styles.input}
                    value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required />
                </Field>
                <Field label="Venue *">
                  <input style={styles.input} placeholder="e.g. The Grand Ballroom, Mumbai"
                    value={form.venue} onChange={e => setForm(f => ({ ...f, venue: e.target.value }))} required />
                </Field>
                <Field label="Description (optional)">
                  <textarea style={{ ...styles.input, minHeight: '70px', resize: 'vertical' }}
                    placeholder="Optional description"
                    value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                </Field>
                {createError && <div style={styles.formError}>{createError}</div>}
                <div style={styles.modalActions}>
                  <button type="button" style={styles.cancelBtn} onClick={() => setShowCreate(false)}>Cancel</button>
                  <button type="submit" style={{ ...styles.submitBtn, ...(creating ? styles.disabled : {}) }} disabled={creating}>
                    {creating ? 'Creating...' : 'Create Event'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Filter tabs */}
        <div style={styles.filterRow}>
          {['all', 'pending', 'indexed', 'matched', 'published'].map(f => (
            <button
              key={f}
              style={{ ...styles.filterBtn, ...(filter === f ? styles.filterBtnActive : {}) }}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={styles.empty}>Loading events…</div>
        ) : filtered.length === 0 ? (
          <div style={styles.empty}>No events found.</div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.thead}>
                  <Th>Event</Th>
                  <Th>Photographer</Th>
                  <Th>Date</Th>
                  <Th>Photos</Th>
                  <Th>Visitors</Th>
                  <Th>Indexed</Th>
                  <Th>Matching</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(e => {
                  const ic = INDEXED_COLORS[e.indexed_status] || INDEXED_COLORS.pending;
                  const sc = STATUS_COLORS[e.status] || STATUS_COLORS.active;
                  return (
                    <tr key={e.id} style={styles.tr}>
                      <td style={{ ...styles.td, fontWeight: '600', color: '#1a1a2e' }}>
                        {e.name}
                        {e.published_at && <span style={styles.publishedDot}>✦</span>}
                      </td>
                      <td style={styles.td}>{e.photographer_name}</td>
                      <td style={styles.td}>{new Date(e.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                      <td style={styles.td}>{e.photo_count}</td>
                      <td style={styles.td}>{e.visitor_count}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, background: ic.bg, color: ic.color }}>{ic.label}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, background: sc.bg, color: sc.color }}>{sc.label}</span>
                      </td>
                      <td style={styles.td}>
                        <button
                          style={styles.viewBtn}
                          onClick={() => navigate(`/admin/super/events/${e.id}`)}
                        >
                          Manage →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const Th = ({ children }) => <th style={styles.th}>{children}</th>;

const Field = ({ label, children }) => (
  <div style={{ marginBottom: '1rem' }}>
    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem' }}>{label}</label>
    {children}
  </div>
);

const styles = {
  page: { minHeight: '100vh', background: '#f8fafc', fontFamily: "'Segoe UI', system-ui, sans-serif" },
  nav: { background: '#1a1a2e', padding: '1rem 2rem', display: 'flex', alignItems: 'center', gap: '1rem' },
  navBrand: { color: '#a78bfa', fontWeight: '800', fontSize: '1.1rem', flex: 1 },
  navUser: { color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' },
  navBtn: { background: 'rgba(167,139,250,0.2)', color: '#a78bfa', border: 'none', borderRadius: '0.5rem', padding: '0.4rem 0.875rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600' },
  logoutBtn: { background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.4rem 0.875rem', cursor: 'pointer', fontSize: '0.875rem' },
  container: { maxWidth: '1200px', margin: '0 auto', padding: '2rem 1.5rem' },
  pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', gap: '1rem' },
  createBtn: { background: '#a78bfa', color: '#1a1a2e', border: 'none', borderRadius: '0.75rem', padding: '0.7rem 1.25rem', fontWeight: '700', cursor: 'pointer', fontSize: '0.95rem', whiteSpace: 'nowrap' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' },
  modal: { background: '#fff', borderRadius: '1.25rem', padding: '2rem', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { fontSize: '1.3rem', fontWeight: '800', color: '#1a1a2e', margin: '0 0 1.25rem' },
  input: { width: '100%', padding: '0.75rem 1rem', border: '1.5px solid #e5e7eb', borderRadius: '0.75rem', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' },
  formError: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', padding: '0.75rem', color: '#dc2626', fontSize: '0.875rem', marginBottom: '0.75rem' },
  modalActions: { display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.25rem' },
  cancelBtn: { padding: '0.7rem 1.25rem', background: '#f3f4f6', border: 'none', borderRadius: '0.75rem', cursor: 'pointer', fontWeight: '600', color: '#444' },
  submitBtn: { padding: '0.7rem 1.5rem', background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '0.75rem', fontWeight: '700', cursor: 'pointer' },
  disabled: { opacity: 0.6, cursor: 'not-allowed' },
  pageTitle: { fontSize: '1.75rem', fontWeight: '800', color: '#1a1a2e', margin: '0 0 0.25rem' },
  pageSubtitle: { color: '#888', margin: 0, fontSize: '0.9rem' },
  filterRow: { display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' },
  filterBtn: { padding: '0.4rem 1rem', background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '50px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600', color: '#888' },
  filterBtnActive: { background: '#1a1a2e', borderColor: '#1a1a2e', color: '#fff' },
  tableWrap: { background: '#fff', borderRadius: '1.25rem', overflow: 'hidden', border: '1px solid #f0f0f0', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '800px' },
  thead: { background: '#f8fafc' },
  th: { padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f5f5f5' },
  td: { padding: '0.875rem 1rem', fontSize: '0.875rem', color: '#555' },
  badge: { display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '50px', fontSize: '0.75rem', fontWeight: '700', whiteSpace: 'nowrap' },
  publishedDot: { marginLeft: '0.4rem', color: '#7c3aed', fontSize: '0.7rem' },
  viewBtn: { padding: '0.35rem 0.875rem', background: '#f0f4ff', color: '#4f46e5', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '700', fontSize: '0.8rem' },
  empty: { textAlign: 'center', padding: '4rem', color: '#888', background: '#fff', borderRadius: '1.25rem', border: '1px solid #f0f0f0' },
};

export default SuperAdminDashboard;
